import React, { useRef, useState, useEffect, useMemo } from 'react';
import { GuessHistoryItem } from '../types/types';
import { CSS_CLASSES } from '../constants';

interface YearSliderProps {
  lowerBound: number;
  upperBound: number;
  minYear: number;
  maxYear: number;
  guessHistory: GuessHistoryItem[];
  actualYear: number | null;
  onTimelineClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  yearInput: string[];
  onSliderChange?: (year: number) => void;
  revealedDigits?: boolean[];
  isRecapMode?: boolean; // New prop to disable cursor effects
  disabled?: boolean; // New prop to disable all interactions
  isSelectingDigit?: boolean; // New prop to indicate digit selection mode for opacity
}

const YearSlider: React.FC<YearSliderProps> = ({
  lowerBound,
  upperBound,
  minYear,
  maxYear,
  guessHistory,
  actualYear,
  onTimelineClick,
  yearInput,
  onSliderChange,
  revealedDigits = [false, false, false, false],
  isRecapMode = false, // Default to false
  disabled = false, // Default to false
  isSelectingDigit = false, // Default to false
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [sliderWidth, setSliderWidth] = useState<number>(0);
  const [preventScroll, setPreventScroll] = useState(false);

  // Store the current year value
  const [currentYear, setCurrentYear] = useState<number>(() => {
    // Parse the yearInput to initialize
    const inputYear = yearInput.join('');
    if (inputYear.length === 4) {
      const parsedYear = parseInt(inputYear);
      if (
        !isNaN(parsedYear) &&
        parsedYear >= minYear &&
        parsedYear <= maxYear
      ) {
        return parsedYear;
      }
    }
    return Math.floor((minYear + maxYear) / 2); // Default to middle year
  });

  // Update slider width on mount and on window resize
  useEffect(() => {
    const updateSliderWidth = () => {
      if (sliderRef.current) {
        setSliderWidth(sliderRef.current.getBoundingClientRect().width);
      }
    };
    updateSliderWidth();
    window.addEventListener('resize', updateSliderWidth);
    return () => window.removeEventListener('resize', updateSliderWidth);
  }, []);

  // Calculate position (in percentage) for a given year
  const calculatePosition = (year: number): number => {
    if (year < minYear) year = minYear;
    if (year > maxYear) year = maxYear;
    const range = maxYear - minYear;
    return ((year - minYear) / range) * 100;
  };

  // Check if a year matches the revealed digits
  const yearMatchesRevealedDigits = (
    yearToCheck: number,
    revealedDigits: boolean[],
    yearInput: string[]
  ): boolean => {
    if (!revealedDigits.some((revealed) => revealed)) return true;

    const yearStr = yearToCheck.toString().padStart(4, '0');

    for (let i = 0; i < 4; i++) {
      if (revealedDigits[i] && yearInput[i] !== yearStr[i]) {
        return false;
      }
    }

    return true;
  };

  // Calculate the valid ranges once when revealed digits change
  const validRanges = useMemo(() => {
    if (!revealedDigits.some((revealed) => revealed)) {
      return [{ start: minYear, end: maxYear }]; // Full range if no digits revealed
    }

    const ranges: { start: number; end: number }[] = [];

    // Find all valid years within the range
    let start: number | null = null;

    // Use a reasonable step size to check years
    const step = Math.max(1, Math.floor((maxYear - minYear) / 1000));

    for (let year = minYear; year <= maxYear; year += step) {
      const isValid = yearMatchesRevealedDigits(
        year,
        revealedDigits,
        yearInput
      );

      if (isValid && start === null) {
        start = year;
      } else if (!isValid && start !== null) {
        ranges.push({ start, end: year - step });
        start = null;
      }
    }

    // Don't forget the last range if it extends to maxYear
    if (start !== null) {
      ranges.push({ start, end: maxYear });
    }

    // If no ranges were found, use a default range
    if (ranges.length === 0) {
      // Create a default range around a valid year if possible
      for (let year = minYear; year <= maxYear; year++) {
        if (yearMatchesRevealedDigits(year, revealedDigits, yearInput)) {
          const range = Math.floor((maxYear - minYear) / 10);
          const start = Math.max(minYear, year - range);
          const end = Math.min(maxYear, year + range);
          return [{ start, end }];
        }
      }

      // If still no valid years, just return the full range
      return [{ start: minYear, end: maxYear }];
    }

    return ranges;
  }, [revealedDigits, yearInput, minYear, maxYear]);

  // Find the nearest valid year that satisfies the revealed digits
  const findNearestValidYear = (yearToCheck: number): number => {
    // First, check if the current year is already valid
    if (yearMatchesRevealedDigits(yearToCheck, revealedDigits, yearInput)) {
      return yearToCheck;
    }

    // If we have valid ranges, find the closest year in any valid range
    if (validRanges.length > 0) {
      let closestYear = minYear;
      let minDistance = Number.MAX_SAFE_INTEGER;

      for (const range of validRanges) {
        // Check if year is within this range
        if (yearToCheck >= range.start && yearToCheck <= range.end) {
          return yearToCheck; // It's already in a valid range
        }

        // Find the closest endpoint of this range
        const distanceToStart = Math.abs(yearToCheck - range.start);
        const distanceToEnd = Math.abs(yearToCheck - range.end);

        if (distanceToStart < minDistance) {
          minDistance = distanceToStart;
          closestYear = range.start;
        }

        if (distanceToEnd < minDistance) {
          minDistance = distanceToEnd;
          closestYear = range.end;
        }
      }

      return closestYear;
    }

    // If no valid ranges, construct a valid year from the revealed digits
    const yearStr = yearToCheck.toString().padStart(4, '0');
    let resultStr = '';

    for (let i = 0; i < 4; i++) {
      resultStr +=
        revealedDigits[i] && yearInput[i] ? yearInput[i] : yearStr[i];
    }

    const result = parseInt(resultStr);
    return Math.max(minYear, Math.min(maxYear, result));
  };

  // Calculate valid year from a position percentage
  const calculateYearFromPosition = (position: number): number => {
    const range = maxYear - minYear;
    let year = Math.round(minYear + (range * position) / 100);

    // Ensure we get a valid year if digits are revealed
    if (revealedDigits.some((revealed) => revealed)) {
      year = findNearestValidYear(year);
    }

    return year;
  };

  // Calculate current position for the slider knob
  const knobPosition = useMemo(() => {
    // If we have a valid year, use it
    if (currentYear >= minYear && currentYear <= maxYear) {
      // Make sure the current year matches revealed digits
      const validYear = revealedDigits.some((revealed) => revealed)
        ? findNearestValidYear(currentYear)
        : currentYear;

      return calculatePosition(validYear);
    }

    // Calculate from yearInput if possible
    const inputYear = yearInput.join('');
    if (inputYear.length === 4) {
      const parsedYear = parseInt(inputYear);
      if (
        !isNaN(parsedYear) &&
        parsedYear >= minYear &&
        parsedYear <= maxYear
      ) {
        return calculatePosition(parsedYear);
      }
    }

    // Default to the middle position
    return 50;
  }, [currentYear, yearInput, minYear, maxYear, revealedDigits]);

  // Update the current year when yearInput changes
  useEffect(() => {
    const inputYear = yearInput.join('');
    if (inputYear.length === 4) {
      const parsedYear = parseInt(inputYear);
      if (
        !isNaN(parsedYear) &&
        parsedYear >= minYear &&
        parsedYear <= maxYear
      ) {
        setCurrentYear(parsedYear);
      }
    }
  }, [yearInput, minYear, maxYear]);

  // Handle position updates (for both mouse and touch)
  const updatePositionFromEvent = (clientX: number) => {
    if (sliderRef.current && !actualYear && !isRecapMode && !disabled) {
      const rect = sliderRef.current.getBoundingClientRect();
      const position = ((clientX - rect.left) / rect.width) * 100;
      const clampedPosition = Math.max(0, Math.min(100, position));
      const year = calculateYearFromPosition(clampedPosition);

      setCurrentYear(year);

      if (onSliderChange) {
        onSliderChange(year);
      }
    }
  };

  // Handle slider click
  const handleSliderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (actualYear || isRecapMode || disabled) return; // Don't allow interaction if year is revealed or in recap mode
    updatePositionFromEvent(e.clientX);
  };

  // Handle mouse down on knob
  const handleMouseDown = (e: React.MouseEvent) => {
    if (actualYear || isRecapMode || disabled) return; // Don't allow dragging if year is revealed or in recap mode

    e.preventDefault();
    setIsDragging(true);
    setPreventScroll(true);

    // Add a class to the body to prevent scrolling
    document.body.classList.add(CSS_CLASSES.NO_SCROLL);
  };

  // Handle touch start on knob
  const handleTouchStart = () => {
    if (actualYear || isRecapMode || disabled) return; // Don't allow dragging if year is revealed or in recap mode

    setIsDragging(true);
    setPreventScroll(true);

    // Add a class to the body to prevent scrolling
    document.body.classList.add(CSS_CLASSES.NO_SCROLL);
  };

  // Handle mouse move
  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && !isRecapMode && !disabled) {
      updatePositionFromEvent(e.clientX);
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    if (!isRecapMode && !disabled) {
      setIsDragging(false);
      setPreventScroll(false);

      // Remove the no-scroll class from the body
      document.body.classList.remove(CSS_CLASSES.NO_SCROLL);
    }
  };

  // Handle touch move
  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging && e.touches.length > 0 && !isRecapMode && !disabled) {
      // If we're dragging, prevent the default scroll behavior
      if (preventScroll) {
        e.preventDefault();
      }
      updatePositionFromEvent(e.touches[0].clientX);
    }
  };

  // Handle touch end
  const handleTouchEnd = () => {
    if (!isRecapMode && !disabled) {
      setIsDragging(false);
      setPreventScroll(false);

      // Remove the no-scroll class from the body
      document.body.classList.remove(CSS_CLASSES.NO_SCROLL);
    }
  };

  // Add mouse event listeners
  useEffect(() => {
    if (!isRecapMode && !disabled) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        // Make sure to clean up the no-scroll class when component unmounts
        document.body.classList.remove(CSS_CLASSES.NO_SCROLL);
      };
    }
    return undefined;
  }, [isDragging, preventScroll, isRecapMode, disabled]);

  // Add touch event listeners
  useEffect(() => {
    if (!isRecapMode && !disabled) {
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);

        // Make sure to clean up the no-scroll class when component unmounts
        document.body.classList.remove(CSS_CLASSES.NO_SCROLL);
      };
    }
    return undefined;
  }, [isDragging, preventScroll, isRecapMode, disabled]);

  // Get positions
  const actualPos = actualYear !== null ? calculatePosition(actualYear) : null;

  // Compute difference line parameters if both markers exist
  let diffLineStyle = {};
  if (actualPos !== null && guessHistory.length > 0) {
    const guessPos = calculatePosition(guessHistory[0].year);
    const leftPos = Math.min(actualPos, guessPos);
    const width = Math.abs(actualPos - guessPos);
    diffLineStyle = {
      left: `calc(${leftPos}% + 4px)`,
      width: `calc(${width}% - 8px)`,
    };
  }

  return (
    <>
      <div
        className={`relative w-full mb-[4.75rem] md:mb-[3.75rem] max-w-[500px] justify-self-center mt-6 ml-auto mr-auto ${isRecapMode || disabled ? 'cursor-default' : ''} ${isSelectingDigit ? 'opacity-50' : ''}`}
        ref={sliderRef}
      >
        {/* Valid ranges highlighting - only shown when digits are revealed */}
        {guessHistory.length === 0 &&
          !actualYear &&
          revealedDigits.some((d) => d) &&
          validRanges.map((range, index) => {
            const startPosPercent = calculatePosition(range.start);
            const endPosPercent = calculatePosition(range.end);
            let widthPercent = endPosPercent - startPosPercent;
            let leftPercent = startPosPercent;

            // Ensure a minimum visible width for narrow ranges
            if (sliderWidth > 0) {
              const computedWidthPx = sliderWidth * (widthPercent / 100);
              const minWidthPx = 4;
              if (computedWidthPx < minWidthPx) {
                const newWidthPercent = (minWidthPx / sliderWidth) * 100;
                const centerPercent = startPosPercent + widthPercent / 2;
                leftPercent = centerPercent - newWidthPercent / 2;
                widthPercent = newWidthPercent;
              }
            }

            return (
              <div
                key={index}
                className="absolute"
                style={{
                  height: '4px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  left: `${leftPercent}%`,
                  width: `${widthPercent}%`,
                  backgroundColor: '#FFFFFF',
                  opacity: 0.4,
                  borderRadius: '2px',
                  zIndex: 1,
                }}
              />
            );
          })}

        {/* Larger click area for the slider track */}
        <div
          className={`absolute inset-0 ${isRecapMode || actualYear || disabled ? 'cursor-default' : 'cursor-pointer'}`}
          style={{
            height: '24px', // Increased height for better tap target
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 3,
          }}
          onClick={
            isRecapMode || actualYear || disabled
              ? undefined
              : handleSliderClick
          }
        />

        {/* Slider Track (visual only - not interactive) */}
        <div
          className="absolute inset-0 bg-[#374151] rounded-full pointer-events-none"
          style={{
            height: '4px',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />

        {/* Slider Knob - Always visible unless actual year is shown */}
        {!actualYear && (
          <>
            {/* Invisible larger tap target */}
            <div
              className={`absolute w-12 h-16 rounded-full ${isRecapMode || disabled ? 'cursor-default' : 'cursor-pointer'}`}
              style={{
                left: `calc(${knobPosition}% - 24px)`,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 5,
              }}
              onMouseDown={
                isRecapMode || disabled ? undefined : handleMouseDown
              }
              onTouchStart={
                isRecapMode || disabled ? undefined : handleTouchStart
              }
            />
            {/* Visible knob (same size as before) */}
            <div
              className={`absolute w-6 h-6 rounded-full bg-white border-2 border-[#7236DC] pointer-events-none transition-all ${isRecapMode || disabled ? '' : isDragging ? 'scale-110 bg-[#dad1e0]' : 'hover:bg-[#dad1e0]'}`}
              style={{
                left: `calc(${knobPosition}% - 12px)`,
                top: '50%',
                transform: 'translateY(-50%)',
                transition: isDragging
                  ? 'none'
                  : 'left 0.1s ease-out, transform 0.1s',
                boxShadow: '0 0 8px rgba(114, 54, 220, 0.4)',
                zIndex: 6,
              }}
            />
          </>
        )}

        {/* Guess markers */}
        {guessHistory.length > 0 && actualYear && (
          <div
            className="absolute w-3 h-3 rounded-full bg-white z-10"
            style={{
              left: `calc(${calculatePosition(guessHistory[0].year)}% - 1px)`,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
            }}
            title={`Your guess: ${guessHistory[0].year}`}
          />
        )}

        {/* Actual Year Marker */}
        {actualYear !== null && (
          <div
            className="absolute w-3 h-3 rounded-full bg-[#C6A4FF]"
            style={{
              left: `calc(${calculatePosition(actualYear)}% - 1.5px)`,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
            }}
            title={`Actual year: ${actualYear}`}
          />
        )}

        {/* Colored difference line */}
        {actualYear !== null && guessHistory.length > 0 && (
          <div
            className="absolute bg-[#C6A4FF]"
            style={{
              height: '2px',
              top: '50%',
              transform: 'translateY(-50%)',
              ...diffLineStyle,
              zIndex: 2,
            }}
          />
        )}

        {/* Year labels for min and max */}
        <div
          className="absolute w-full flex justify-between"
          style={{ marginTop: '8px' }}
        >
          {!guessHistory.length && (
            <>
              <span className="uppercase text-[12px] text-white/70 font-bold">
                {minYear}
              </span>
              <span className="uppercase text-[12px] text-white/70 font-bold">
                {maxYear}
              </span>
            </>
          )}
        </div>

        {/* Year labels for guesses and actual year */}
        {actualYear !== null && (
          <div
            className="absolute text-[#C6A4FF] text-xs font-bold"
            style={{
              left: `calc(${calculatePosition(actualYear)}% - 10px)`,
              top: '0',
              marginTop: '9px',
            }}
          >
            {actualYear}
          </div>
        )}

        {/* User's guess year label (now positioned above the slider) */}
        {guessHistory.length > 0 && actualYear && (
          <div
            className="absolute text-white text-xs font-bold"
            style={{
              left: `calc(${calculatePosition(guessHistory[0].year)}% - 10px)`,
              bottom: '100%',
              marginBottom: '8px',
            }}
          >
            {guessHistory[0].year}
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(YearSlider);
